/**
 * Timer display component - Shows countdown and current period
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { timerService } from '../services/timer.js';
import { databaseService } from '../services/database.js';
import type { TimerState } from '../models/types.js';
import { formatCountdown, formatPeriodRange } from '../utils/time.js';

@customElement('timer-display')
export class TimerDisplay extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .container {
      padding: 0.5rem;
    }

    .period-type {
      font-size: 0.75rem;
      font-weight: 400;
      color: var(--text-secondary);
      margin-bottom: 0.25rem;
      text-transform: lowercase;
      letter-spacing: 0;
    }

    .period-type.activity {
      color: var(--color-activity);
    }

    .period-type.rest {
      color: var(--color-rest);
    }

    .period-range {
      font-size: 0.85rem;
      color: var(--text-tertiary);
      margin-bottom: 0.5rem;
    }

    .countdown {
      font-size: 3rem;
      font-weight: 400;
      color: var(--text-primary);
      font-variant-numeric: tabular-nums;
      margin-bottom: 0.5rem;
      font-family: inherit;
    }

    .progress-container {
      max-width: 100%;
      margin: 0 0 0.5rem 0;
    }

    .progress-bar {
      width: 100%;
      height: 4px;
      background-color: var(--bg-tertiary);
      border-radius: 0;
      overflow: hidden;
      box-shadow: none;
    }

    .progress-fill {
      height: 100%;
      transition: width 0.5s ease;
      border-radius: 0;
    }

    .progress-fill.activity {
      background-color: var(--color-activity);
    }

    .progress-fill.rest {
      background-color: var(--color-rest);
    }

    .info {
      font-size: 0.75rem;
      color: var(--text-secondary);
      margin-top: 0.5rem;
      line-height: 1.3;
      text-align: left;
    }

    .info strong {
      display: inline;
      color: var(--text-primary);
      margin-right: 0.5rem;
    }

    .card {
      background-color: var(--bg-secondary);
      border-radius: 0;
      padding: 0.5rem;
      border: 1px solid var(--color-border);
    }

    .check-in-button {
      margin-top: 0.5rem;
      padding: 0.5rem 1rem;
      background-color: var(--color-primary);
      color: white;
      border: none;
      cursor: pointer;
      width: 100%;
      font-size: 0.9rem;
    }

    .check-in-button:hover {
      background-color: var(--color-primary-hover);
    }
  `;

  @state() private timerState: TimerState | null = null;
  @state() private currentPlan: string | null = null;

  async connectedCallback() {
    super.connectedCallback();

    // Subscribe to timer updates
    timerService.subscribe((event) => {
      this.timerState = event.state;

      // Reload plan when boundary is crossed
      if (event.type === 'boundary') {
        this.loadCurrentPlan();
      }
    });

    // Load current period's plan
    await this.loadCurrentPlan();
  }

  private async loadCurrentPlan() {
    const lastCheckIn = await databaseService.getLastCheckIn();
    if (!lastCheckIn) {
      this.currentPlan = null;
      return;
    }

    // Check if the last check-in's planned period matches the current period
    const currentPeriod = this.timerState?.currentPeriod;
    if (!currentPeriod) return;

    const currentPeriodStart = currentPeriod.start.getTime();
    if (lastCheckIn.nextPlannedPeriod.start === currentPeriodStart) {
      this.currentPlan = lastCheckIn.futureDescription;
    } else {
      this.currentPlan = null;
    }
  }

  private handleCheckInClick() {
    this.dispatchEvent(new CustomEvent('request-check-in', {
      bubbles: true,
      composed: true
    }));
  }

  render() {
    if (!this.timerState) {
      return html`<div class="container">loading...</div>`;
    }

    const { currentPeriod, timeRemaining, isActivity } = this.timerState;
    const periodType = isActivity ? 'activity' : 'rest';

    // Calculate progress percentage
    const totalDuration = currentPeriod.end.getTime() - currentPeriod.start.getTime();
    const elapsed = totalDuration - timeRemaining;
    const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

    return html`
      <div class="container">
        <div class="card">
          <div class="period-type ${periodType}">
            ${isActivity ? 'activity' : 'rest'}
          </div>

          <div class="period-range">
            ${formatPeriodRange(currentPeriod)}
          </div>

          <div class="countdown">
            ${formatCountdown(timeRemaining)}
          </div>

          <div class="progress-container">
            <div class="progress-bar">
              <div
                class="progress-fill ${periodType}"
                style="width: ${progressPercent}%"
              ></div>
            </div>
          </div>

          ${this.currentPlan ? html`
            <div class="info">
              <strong>plan:</strong> ${this.currentPlan}
            </div>
          ` : ''}

          <button class="check-in-button" @click=${this.handleCheckInClick}>
            check in now
          </button>
        </div>
      </div>
    `;
  }
}
